/**
 * What Settings › Calendar and the coach's other pages know about her calendar
 * connection, and the choices she can save. Safe on the client.
 *
 * Aliases: calendar settings, calendar connection view, calendar choices
 */

import { z } from "zod";

import { MeetingProvider } from "~/lib/schemas/db-enums";

/** One of her calendars, as Settings › Calendar offers it. */
export interface CalendarChoice {
  id: string;
  name: string;
  primary: boolean;
  /** She can add events to it, so it can receive DCM's events. */
  canWrite: boolean;
}

/** Settings › Calendar, by where her connection stands. */
export type CalendarSettings =
  | { state: "not_connected" }
  | {
      state: "needs_reconnect";
      accountEmail: string | null;
      upcomingSessionsInCalendar: number;
    }
  | {
      state: "connected";
      accountEmail: string | null;
      calendars: CalendarChoice[];
      writeCalendarId: string | null;
      busyCalendarIds: string[];
      defaultMeetingProvider: MeetingProvider;
      /** The main calendar's time zone, offered while she has none saved. */
      calendarTimeZone: string | null;
      /** Sessions still to come whose event DCM keeps in her calendar. */
      upcomingSessionsInCalendar: number;
    };

/** Whether she has the calendar page, and whether to ask her to reconnect. */
export interface CalendarSummary {
  available: boolean;
  needsReconnect: boolean;
}

export const calendarChoicesSchema = z.object({
  writeCalendarId: z.string().min(1).max(1024),
  busyCalendarIds: z.array(z.string().min(1).max(1024)).max(100),
  defaultMeetingProvider: z.nativeEnum(MeetingProvider),
});
export type CalendarChoices = z.output<typeof calendarChoicesSchema>;

/**
 * One of her own events, shown to her alone in the session scheduler. Never
 * stored.
 */
export interface SchedulerCalendarEvent {
  start: Date;
  end: Date;
  title: string | null;
  allDay: boolean;
}

/** The longest span the scheduler asks her calendar for at once. */
export const SCHEDULER_EVENTS_MAX_DAYS = 2;

export const schedulerEventsRangeSchema = z
  .object({ start: z.string().datetime(), end: z.string().datetime() })
  .refine(
    ({ start, end }) =>
      new Date(end).getTime() > new Date(start).getTime() &&
      new Date(end).getTime() - new Date(start).getTime() <=
        SCHEDULER_EVENTS_MAX_DAYS * 24 * 60 * 60 * 1000,
    { message: "Ask for one day at a time." },
  );

/**
 * The query parameter her sign-in returns her with, so the page can say the
 * last attempt did not connect.
 */
export const CALENDAR_RETURN_PARAM = "connect";

/**
 * Cookie that steers the E2E server's in-memory calendar (see
 * `~/lib/server/e2e/fake-calendar`). Only read when `E2E_MOCK_AI=1`.
 */
export const E2E_CALENDAR_COOKIE = "dcm_e2e_calendar";
export const E2E_CALENDAR_SCENARIOS = [
  "refuse",
  "abandon",
  "revoked",
  "busy",
  "write-fails",
] as const;
export type E2ECalendarScenario = (typeof E2E_CALENDAR_SCENARIOS)[number];
